import { useEffect, useRef, useState } from 'react';
import { type Room, RoomEvent } from 'livekit-client';
import { notifyCallJoin } from '@/services/calls/client';

/**
 * Whether the outbound call has actually been *answered* by the other party.
 *
 * A participant joining the room is not "answered" — the bridged leg is added
 * while it's still dialing/ringing. The real signal differs per channel, and
 * this hook latches `true` as soon as either one is seen:
 *
 * - **WhatsApp** — not a SIP leg, so the backend stamps the room metadata with
 *   `call_status: 'answered'` on Meta's connect webhook (see
 *   `LiveKitService::markRoomAnswered` in the universe backend).
 * - **PSTN** — a LiveKit SIP participant whose `sip.callStatus` attribute
 *   transitions `dialing → ringing → active`; `active` means answered. LiveKit
 *   pushes attribute changes to the client in real time.
 *
 * For PSTN it also notifies the backend (`POST /call/join`) with the answered
 * SIP participant, so the server records the authoritative state and starts
 * recording — fired once, since only the live client can tell the real answer
 * moment (the webhook can't). WhatsApp answer is already known server-side, so
 * it isn't notified from here.
 *
 * @param room The connected LiveKit room (from `useRoomContext`).
 */
export function useCallAnswered(room: Room): boolean {
    const [answered, setAnswered] = useState(false);
    const notifiedRef = useRef(false);

    useEffect(() => {
        // Tell the backend the leg was answered — once, with a small retry.
        const notifyJoin = (roomName: string, identity: string) => {
            if (notifiedRef.current || !roomName || !identity) return;
            notifiedRef.current = true;
            void (async () => {
                for (let attempt = 1; attempt <= 3; attempt++) {
                    try {
                        await notifyCallJoin(roomName, identity);
                        return;
                    } catch (e) {
                        console.log(`Call join notify failed (attempt ${attempt})`, e);
                        await new Promise((resolve) => setTimeout(resolve, 1500));
                    }
                }
            })();
        };

        const check = () => {
            // WhatsApp: room metadata flipped by the backend (already known server-side).
            try {
                const md = room.metadata ? JSON.parse(room.metadata) : null;
                if (md?.call_status === 'answered') {
                    setAnswered(true);
                    return;
                }
            } catch {
                // metadata may be non-JSON; ignore
            }
            // PSTN: the dialed SIP participant reports it's connected.
            for (const p of room.remoteParticipants.values()) {
                if (p.attributes?.['sip.callStatus'] === 'active') {
                    setAnswered(true);
                    notifyJoin(room.name, p.identity);
                    return;
                }
            }
        };

        // Re-check on (re)connect as well: livekit-client only emits
        // RoomMetadataChanged when metadata *changes* after the join — a room
        // that already carries `answered` when we join (slow join, or a rejoin
        // after a reconnect) produces no event, and the initial check() below
        // runs before we're connected, when `room.metadata` is still empty.
        // Missing it left the ringback playing over an answered call.
        check();
        room.on(RoomEvent.Connected, check);
        room.on(RoomEvent.Reconnected, check);
        room.on(RoomEvent.RoomMetadataChanged, check);
        room.on(RoomEvent.ParticipantAttributesChanged, check);
        room.on(RoomEvent.ParticipantConnected, check);
        return () => {
            room.off(RoomEvent.Connected, check);
            room.off(RoomEvent.Reconnected, check);
            room.off(RoomEvent.RoomMetadataChanged, check);
            room.off(RoomEvent.ParticipantAttributesChanged, check);
            room.off(RoomEvent.ParticipantConnected, check);
        };
    }, [room]);

    return answered;
}
